import { I18n } from 'common/i18n';
import { SoQLStringLiteral, TypedSoQLStringLiteral, Expr, TypedExpr, TypedSelect, UnAnalyzedAst, BinaryTree } from 'common/types/soql';
import SoqlDataProvider from 'common/visualizations/dataProviders/SoqlDataProvider';
import { getSuggestions, getSuggestionsNA, Suggester } from '../../lib/data-helpers';
import { getUnAnalyzedAst, getAstFromAnalysis } from '../../lib/selectors';
import { AppState } from '../../redux/store';
import _ from 'lodash';
import { Option } from 'ts-option';
import { default as React } from 'react';
import { connect } from 'react-redux';
import { AstNode, ExprProps } from '../VisualExpressionEditor';
import { withSubExprUpdates } from './Argument';
import { renderKebabButton } from './EditLiteral';
import { PhxChannel } from 'common/types/dsmapi';
import { ForgeAutocomplete, ForgeTextField, ForgeIcon } from '@tylertech/forge-react';
import { IAutocompleteOption } from '@tylertech/forge';
import { usingSoda3EC, whichAnalyzer } from 'common/explore_grid/lib/feature-flag-helpers';
import { Either, factorOption } from 'common/either';
import { ClientContextVariable } from 'common/types/clientContextVariable';
import { getReplacing } from '../../redux/actions';

const t = (k: string) => I18n.t(k, { scope: 'shared.explore_grid.string_literal_with_suggestions' });

type ExternalProps = ExprProps<SoQLStringLiteral, TypedSoQLStringLiteral> & {
  suggestionTarget: Either<Expr, TypedExpr>;
};
type Props = ExternalProps & {
  tree: Option<Either<BinaryTree<UnAnalyzedAst>, BinaryTree<TypedSelect>>>;
  channel: PhxChannel;
  fourfour: string;
  queryContext: {
    clientContextInfo: { variables: ClientContextVariable[] };
    replacing: string | null;
  }
};

class StringLiteralWithSuggestions extends React.Component<Props> {
  suggester: Option<Suggester>;

  constructor(props: Props) {
    super(props);
    this.buildSuggester(props);
  }

  buildSuggester = (props: Props) => {
    const { queryContext, fourfour, scope, channel, suggestionTarget } = props;

    const provider = new SoqlDataProvider({
      domain: window.location.host,
      datasetUid: fourfour,
    });

    this.suggester = this.props.tree.map(eitherTree => {
      return eitherTree.fold(
        tree => getSuggestions(provider, suggestionTarget.left, scope, tree, channel),
        tree => {
          const { replacing, clientContextInfo } = queryContext;
          return getSuggestionsNA(provider, suggestionTarget.right, tree, clientContextInfo, replacing, channel);
        }
      );
    });
  };

  componentDidUpdate(newProps: Props) {
    if (!_.isEqual(newProps.suggestionTarget, this.props.suggestionTarget)) {
      this.buildSuggester(newProps);
    }
  }


  onRemove = () => {
    this.props.remove();
  };

  onUpdate = (newValue: string) => {
    const candidate = this.props.eexpr.mapBoth(
      eexpr => ({ ...eexpr.untyped, value: newValue }),
      eexpr => ({ ...eexpr.expr, value: newValue })
    );
    if (usingSoda3EC()) { // This could probably be smarter, but also, it'll get collapsed when we transition off the feature flag anyways.
      if (newValue !== this.props.eexpr.right.expr.value) {
        this.props.update(candidate);
      }
    } else {
      if (newValue !== this.props.eexpr.left.untyped.value) {
        this.props.update(candidate);
      }
    }
  };

  fetchSuggestions = (filterText: string): Promise<IAutocompleteOption<string>[]> => {
    return this.suggester
    .map(suggest =>
      suggest(filterText, 0, true)
      .then(
        suggestions => suggestions.results
        .map(
          res => ({label: res.title, value: res.title})
        ))
    )
    .getOrElseValue(Promise.resolve([]));
  };

  renderAutocomplete() {
    const expr = this.props.eexpr.fold(
      eexpr => eexpr.untyped,
      eexpr => eexpr.expr
    );

    return (
      (<ForgeAutocomplete
        mode="stateless"
        allowUnmatched={true}
        filterFocusFirst={false}
        filterOnFocus={true}
        filter={this.fetchSuggestions}
        value={expr.value || ''}
        on-forge-autocomplete-select={(evt: CustomEvent) => this.onUpdate(evt.detail.value)}
        data-testid="string-literal-with-suggestions-autocomplete"
      >
        <ForgeTextField>
          <ForgeIcon slot="start" name="search" />
          <input
            type="text"
            id={'string-with-literal-suggestions-input'}
            data-testid={'string-with-literal-suggestions-input'}
          />
          <label htmlFor='string-with-literal-suggestions-input'>{t('text_value')}</label>
        </ForgeTextField>
      </ForgeAutocomplete>)
    );
  }

  render() {
    const { showKebab, showRemove } = this.props;
    const changeableElement = showKebab ? 1 : undefined;
    const removable = showRemove !== undefined && showRemove;
    return (
      <AstNode {...this.props} className="literal string-literal-with-suggestions" changeableElement={changeableElement} removable={removable}>
        {this.renderAutocomplete()}
        {renderKebabButton(showKebab)}
      </AstNode>
    );
  }
}

const mapStateToProps = (state: AppState, props: ExternalProps): Props => {
  const queryContext = {
    clientContextInfo: state.clientContextInfo,
    replacing: getReplacing(state),
  };
  return {
    ...props,
    queryContext,
    fourfour: state.fourfourToQuery,
    tree: factorOption<BinaryTree<UnAnalyzedAst>, BinaryTree<TypedSelect>>(whichAnalyzer(
      getUnAnalyzedAst,
      getAstFromAnalysis
    )(state.query)),
    channel: state.channel
  };
};

export default withSubExprUpdates(connect(mapStateToProps)(StringLiteralWithSuggestions));
